import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import type { Holding, Order } from '@/api/generated/model';
import { getAccountHoldings, getAccountOrders } from '@/api/generated/trading/trading';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { flattenPositions, portfolioTotals, type PositionRow } from './positionHelpers';
import { parseTradeTab, type TradeTab } from './types';

export function useTradePage(tabParam: string | undefined) {
  const activeTab = parseTradeTab(tabParam);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  const { data: accountsResponse, isLoading: accountsLoading } = useGetAllAccounts();
  const accounts = useMemo(() => accountsResponse?.items ?? [], [accountsResponse]);
  const tradingAccounts = useMemo(
    () => accounts.filter((a) => a.capabilities?.includes('trading')),
    [accounts]
  );

  const holdingsQueries = useQueries({
    queries: tradingAccounts.map((account) => ({
      queryKey: [`/api/accounts/${account.id}/holdings`],
      queryFn: () => getAccountHoldings(account.id),
      enabled: !!account.id,
    })),
  });

  const ordersQueries = useQueries({
    queries: tradingAccounts.map((account) => ({
      queryKey: [`/api/accounts/${account.id}/orders`],
      queryFn: () => getAccountOrders(account.id),
      enabled: !!account.id,
    })),
  });

  const holdingsByAccount = useMemo(() => {
    const map: Record<string, Holding[]> = {};
    tradingAccounts.forEach((account, index) => {
      map[account.id] = holdingsQueries[index]?.data ?? [];
    });
    return map;
  }, [tradingAccounts, holdingsQueries]);

  const ordersByAccount = useMemo(() => {
    const map: Record<string, Order[]> = {};
    tradingAccounts.forEach((account, index) => {
      map[account.id] = ordersQueries[index]?.data ?? [];
    });
    return map;
  }, [tradingAccounts, ordersQueries]);

  const positions: PositionRow[] = useMemo(
    () => flattenPositions(accounts, holdingsByAccount),
    [accounts, holdingsByAccount]
  );

  const allOrders = useMemo(
    () =>
      Object.values(ordersByAccount)
        .flat()
        .sort((a, b) => (b.placedAt ?? '').localeCompare(a.placedAt ?? '')),
    [ordersByAccount]
  );

  const totals = portfolioTotals(positions);
  const isLoading =
    accountsLoading ||
    holdingsQueries.some((q) => q.isLoading) ||
    ordersQueries.some((q) => q.isLoading);

  const ticketAccountId = selectedAccountId || tradingAccounts[0]?.id || '';

  return {
    activeTab: activeTab as TradeTab,
    accounts,
    tradingAccounts,
    positions,
    allOrders,
    totals,
    isLoading,
    ticketAccountId,
    setSelectedAccountId,
  };
}

export function useOverviewHoldings() {
  const { data: accountsResponse, isLoading: accountsLoading } = useGetAllAccounts();
  const accounts = useMemo(() => accountsResponse?.items ?? [], [accountsResponse]);
  const tradingAccounts = useMemo(
    () =>
      accounts.filter(
        (a) => a.capabilities?.includes('investing') || a.capabilities?.includes('trading')
      ),
    [accounts]
  );

  const holdingsQueries = useQueries({
    queries: tradingAccounts.map((account) => ({
      queryKey: [`/api/accounts/${account.id}/holdings`],
      queryFn: () => getAccountHoldings(account.id),
      enabled: !!account.id,
    })),
  });

  const holdings = useMemo(() => holdingsQueries.flatMap((q) => q.data ?? []), [holdingsQueries]);

  const isLoading = accountsLoading || holdingsQueries.some((q) => q.isLoading);

  return { holdings, isLoading };
}
